import { useDirectMessagesChatChannels } from '@modules/chat-channels/hooks/useDirectMessagesChatChannels';
import { useSendChannelMessage } from '@modules/chat-channels/hooks/useSendChannelMessage';
import { useDirectMessagesStatuses } from '@modules/chat-channels/hooks/useUserDirectMessagesStatuses';
import { useConversationEditorConfig } from '@modules/conversation/hooks/useConversationEditorConfig';
import { useSnackbar } from 'notistack';
import type { IWorkspaceUser } from 'src/@types/workspace-info';
import { useLocales } from 'src/locales';

export const useDirectMessageControl = (user: IWorkspaceUser) => {
  const { enqueueSnackbar } = useSnackbar();
  const { translate } = useLocales();

  const directMessagesChannels = useDirectMessagesChatChannels({
    includeHidden: true,
  });

  const {
    resolveChatChannel,
    handleGoToChat: goToChat,
    checkStatusWithUser,
    chatLoading,
  } = useDirectMessagesStatuses(directMessagesChannels);

  const { prepareMessageText, sendMessage, isSending } =
    useSendChannelMessage();

  const { editorConfig } = useConversationEditorConfig({
    participants: [], //? its okay because there are no mention in a DM
    enableSpaceForAi: false,
    enableSlashCommand: false,
    enableCodeBlock: false,
    placeholder: translate('communicate.create_modal.send_dm'),
  });

  const handleSendMessage = async (messageContent: string) => {
    try {
      const chatChannel = await resolveChatChannel(user._id);
      const message = prepareMessageText(chatChannel!._id, messageContent);
      await sendMessage(message);
      enqueueSnackbar(
        translate('communicate.create_modal.message_sent_successfully'),
        {
          variant: 'success',
        },
      );
    } catch (error) {
      enqueueSnackbar(
        translate('communicate.create_modal.failed_to_send_message'),
        {
          variant: 'error',
        },
      );
    }
  };

  const handleGoToChat = () => goToChat(user._id);

  return {
    handleSendMessage,
    chatLoading,
    editorConfig,
    handleGoToChat,
    isLoading: isSending || chatLoading,
    dmChannelStatus: checkStatusWithUser(user._id),
  };
};

export type DirectMessageControl = ReturnType<typeof useDirectMessageControl>;
